import { StanBadge, StanDataRow } from '@/components';
import type { DataRowActionButton, StanDataColumn } from '@/components/core-components/StanDataRow/StanDataRow.types';
import { RiMailSendLine } from 'react-icons/ri';
import ClockIconSvg from '@/assets/icons/ic-clock.svg';
import {
    getAnalyticsStats,
    getBroadcastName,
    getBroadcastStatus,
    getBroadcastStatusText,
} from '../logic/broadcast.logic';
import { formatEmailSubjectPlainText, formatEmailSubjectVariables } from '../../logic/templateVariables.logic';
import type { FlowSummary } from '@/lib/api/email-flows/types';
import { BroadcastActionsMenu } from './BroadcastActionsMenu';
import { cn, createIconElement } from '@/lib';
import { useBroadcastActions } from '../hooks/useBroadcastActions';
import { useIsMobile } from '@/hooks/useMediaQuery';

interface BroadcastItemProps {
    broadcast: FlowSummary;
    gridTemplate: string;
    isBeingDeleted?: boolean;
    isNewlyDuplicated?: boolean;
    onDelete: (flowId: number, flowName: string) => void;
}

export const BroadcastItem = ({
    broadcast,
    gridTemplate,
    isBeingDeleted = false,
    isNewlyDuplicated = false,
    onDelete,
}: BroadcastItemProps) => {
    const ClockIcon = createIconElement(ClockIconSvg);
    const isMobile = useIsMobile();
    // Component handles its own actions (except delete which needs shared modal state)
    const { navigateToBroadcastEditor, handleEdit, handleDuplicate, handleViewRecipients, isDuplicating } =
        useBroadcastActions();

    const buildBroadcastColumns = (): StanDataColumn[] => {
        const name = getBroadcastName(broadcast);
        const status = getBroadcastStatus(broadcast);
        const statusText = getBroadcastStatusText(broadcast);
        const analytics = getAnalyticsStats(broadcast);

        // Get formatted subject with styled variable items
        const subject = broadcast.flow_emails?.[0]?.data?.subject || '';
        const formattedSubject = subject ? formatEmailSubjectVariables(subject) : name;

        return [
            {
                id: 'name',
                name: 'Name',
                value: (
                    <div className='flex items-center gap-2'>
                        <RiMailSendLine className='text-dark size-5 flex-shrink-0' />
                        <span
                            className='text-body-2-bold text-dark truncate pr-2.5'
                            dangerouslySetInnerHTML={{ __html: formattedSubject }}
                        />
                    </div>
                ),
            },
            {
                id: 'sent',
                name: 'Sent',
                value: <span className='text-label-sm-regular text-dark'>{analytics.sent.count}</span>,
                className: 'hidden md:block',
            },
            {
                id: 'opens',
                name: 'Opens',
                value: <span className='text-label-sm-regular text-dark'>{analytics.opens.rate}</span>,
                className: 'hidden md:block',
            },
            {
                id: 'clicks',
                name: 'Clicks',
                value: <span className='text-label-sm-regular text-dark'>{analytics.clicks.rate}</span>,
                className: 'hidden md:block',
            },
            {
                id: 'status',
                name: 'Status',
                value: (
                    <StanBadge
                        variant={status === 'sent' ? 'success' : status === 'scheduled' ? 'primary' : 'muted'}
                        size='sm'
                        icon={(status === 'sent' || status === 'scheduled') && ClockIcon}
                    >
                        {statusText}
                    </StanBadge>
                ),
                className: 'hidden md:block',
            },
        ];
    };

    const columns = buildBroadcastColumns();
    const actionButtons: DataRowActionButton[] = [
        {
            id: 'broadcast-actions',
            type: 'dropdown',
            label: 'Broadcast Actions',
            menu: (
                <BroadcastActionsMenu
                    broadcast={broadcast}
                    onEdit={() => handleEdit(broadcast)}
                    onDelete={() => {
                        const emailSubject = broadcast.flow_emails?.[0]?.data?.subject || '';
                        const cleanSubject = formatEmailSubjectPlainText(emailSubject) || 'Untitled Broadcast';
                        onDelete(broadcast.flow_id, cleanSubject);
                    }}
                    onDuplicate={() => handleDuplicate(broadcast.flow_id)}
                    onViewRecipients={() => handleViewRecipients(broadcast)}
                    isDuplicating={isDuplicating}
                />
            ),
        },
    ];

    return (
        <div
            className={cn(
                'bg-bg-canvas shadow-stan! rounded-lg transition-all duration-500 ease-out',
                isBeingDeleted && '!mt-0 !mb-0 max-h-0 overflow-hidden opacity-0',
                !isBeingDeleted && 'max-h-screen',
                isNewlyDuplicated && 'animate-bounce-in'
            )}
        >
            <StanDataRow
                columns={columns}
                gridTemplate={gridTemplate}
                variant='simple'
                actionButtons={actionButtons}
                onClick={() => navigateToBroadcastEditor(broadcast)}
                className={cn('cursor-pointer', isMobile ? 'flow-row-mobile min-h-32' : 'h-20')}
            />
        </div>
    );
};
